"use client";

import React, { Dispatch, SetStateAction, useState } from "react";
import { useForm } from "react-hook-form";
import { User } from "@/types";
import { Dismiss20Filled } from '@fluentui/react-icons';
import { GoogleLogo, FacebookLogo, Envelope } from '@phosphor-icons/react';
import ContinueWithEmail from "./ContinueWithEmail";
import { useRouter } from "next/navigation";
import pb from "@/lib/pocketbase";
import { toast } from "react-toastify";
import { getAuthMethods, loginWithCookie } from "@/app/actions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { title } from "@/constants";

function ModalSignUp({
  setUser,
}: {
  setUser: Dispatch<SetStateAction<User | undefined>>;
}) {
  const {
    register,
    reset,
    watch,
  } = useForm();
  
  const [showEmailForm, setShowEmailForm] = useState(false);
  const email = watch("email");
  const router = useRouter();
  const queryClient = useQueryClient();

  const { data: authProviders = [] } = useQuery({
    queryKey: ['authProviders'],
    queryFn: async () => {
      try {
        const authMethods = await getAuthMethods()
        return authMethods.map(provider => provider);
      } catch (error) {
        console.error('Failed to get auth providers:', error);
        return [];
      }
    }
  });

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // client side auth
  const handleGoogleSignup = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const authData: any = await pb.collection('user').authWithOAuth2({
        provider: 'google',
        createData: {
          emailVisibility: true,
          role: 'User',
        }
      });
      
      if (authData) {
        // Update the user record with name data from OAuth
        await pb.collection('user').update(authData.record.id, {
          displayName: authData.meta.name,
          firstName: authData.meta.rawUser.given_name,
          lastName: authData.meta.rawUser.family_name
        });

        setUser(authData.record as User);
        await loginWithCookie(pb.authStore.exportToCookie());
        queryClient.setQueryData(['user-header'], authData.record);
        document.getElementById("sign-up-modal")?.click();
        // New Google users land on the registration confirmation/welcome page;
        // returning users go straight to their account. Google verifies email,
        // so no inbox-confirmation step is needed for this provider.
        router.push(authData.meta?.isNew ? "/account/profile?new-registration=true" : "/account");
      }
    } catch (error) {
      console.error('OAuth authentication error:', error);
      console.log((error as any)?.originalError);
      toast.error("Failed to authenticate with Google", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
  };

  const handleFacebookSignup = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const authData: any = await pb.collection('user').authWithOAuth2({
        provider: 'facebook',
        createData: {
          emailVisibility: true,
          role: 'User',
        }
      });
      
      if (authData) {
        // Update the user record with name data from OAuth
        await pb.collection('user').update(authData.record.id, {
          displayName: authData.meta.name,
          firstName: authData.meta.rawUser.given_name,
          lastName: authData.meta.rawUser.family_name
        });

        setUser(authData.record as User);
        await loginWithCookie(pb.authStore.exportToCookie());
        queryClient.setQueryData(['user-header'], authData.record);
        if (typeof window !== "undefined" ) {
        window.location.href = authData.meta?.isNew ? "/account/profile?new-registration=true" : "/account";
        }
      }
    } catch (error) {
      console.error('OAuth authentication error:', error);
      console.log((error as any)?.originalError);
      toast.error("Failed to authenticate with Facebook", {
        position: "bottom-left",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
  };

  const handleContinueWithEmail = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isValidEmail(email)) {
      setShowEmailForm(true);
    }
  };

  return (
    <>
      <input
        type="checkbox"
        id="sign-up-modal"
        className="modal-toggle"
        name=""
        onClick={(e) => {
          e.stopPropagation();
          reset();
          setShowEmailForm(false);
        }}
      />
      <label htmlFor="sign-up-modal" className="modal cursor-pointer" onClick={(e) => e.stopPropagation()}>
        <label className="modal-box relative max-w-full md:max-w-[550px] py-4 px-3 md:p-6" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-end pb-2 select-none">
            <label
              htmlFor="sign-up-modal"
              className="cursor-pointer text-base-content"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                reset();
                setShowEmailForm(false);
                document.getElementById("sign-up-modal")?.click();
              }}
            >
              <Dismiss20Filled />
            </label>
          </div>
          <div className="flex flex-grow flex-col h-[30rem] lg:h-full overflow-y-scroll">
            <div className="w-[100%] bg-gradient-to-r from-primary to-secondary px-6 mt-3 pb-6 rounded-lg text-primary-content">
              <h3 className="pb-1 text-3xl font-bold md:text-3xl pt-6">
                Signup To {title}
              </h3>
              <p className="text-sm md:text-base">
                Select an option from below
              </p>
            </div>

            {!showEmailForm ? (
              <div className="flex flex-col gap-4 mt-6 px-4">
                {authProviders.includes('google') && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      handleGoogleSignup(e);
                    }}
                    className="btn btn-outline gap-2 w-full"
                  >
                    <GoogleLogo size={24} weight="fill" />
                    Sign up with Google
                  </div>
                )}

                {authProviders.includes('facebook') && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFacebookSignup(e);
                    }}
                    className="btn btn-outline gap-2 w-full"
                  >
                    <FacebookLogo size={24} weight="fill" />
                    Sign up with Facebook
                  </button>
                )}

                {(authProviders.includes('google') || authProviders.includes('facebook')) && (
                  <div className="divider">OR</div>
                )}

                <div className="relative">
                  <input
                    type="email"
                    className="py-3 px-4 block w-full bg-base-200 text-base-content border-primary/40 rounded-lg text-sm focus:border-secondary focus:ring-secondary"
                    placeholder="Enter your email..."
                    {...register("email")}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Envelope 
                    size={20} 
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-base-content/60"
                  />
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleContinueWithEmail(e);
                  }}
                  disabled={!email || !isValidEmail(email)}
                  className="btn btn-primary w-full"
                >
                  Continue with Email
                </button>

                <div className="text-sm text-center mt-4">
                  Already have an account?{" "}
                  <span
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      document.getElementById("sign-up-modal")?.click();
                      document.getElementById("sign-in-modal")?.click();
                    }}
                    className="text-primary hover:text-primary/60 cursor-pointer"
                  >
                    Sign in
                  </span>
                </div>
              </div>
            ) : (
              <ContinueWithEmail 
                email={email}
                setUser={setUser}
                onBack={() => setShowEmailForm(false)}
              />
            )}
          </div>
        </label>
      </label>
    </>
  );
}

export default ModalSignUp;